import { useEffect, useMemo, useState } from "react";
import { Input, Select, useToast } from "@astrum/ui";
import type { CustomField } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { parseNumberInput } from "../lib/custom-fields.js";

type Values = Record<string, string | number>;
type Person = { id: string; displayName: string };

interface Props {
  taskId: string;
  projectId: string | null;
  values: Values;
  canEdit: boolean;
  people: Person[];
  onChanged: (values: Values) => void;
}

/**
 * The project's custom fields on a task. Nothing renders for a task outside a
 * project, or a project with no fields. Text and number save when the field is
 * left (so half a number is never sent); the pickers save on change.
 */
export function TaskCustomFields({ taskId, projectId, values, canEdit, people, onChanged }: Props) {
  const { toast } = useToast();
  const [fields, setFields] = useState<CustomField[]>([]);

  useEffect(() => {
    if (!projectId) {
      setFields([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ fields: CustomField[] }>(`/projects/${projectId}/custom-fields`)
      .then((res) => !cancelled && setFields(res.fields))
      .catch(() => !cancelled && setFields([]));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const save = async (field: CustomField, value: string | number | null): Promise<boolean> => {
    try {
      const res = await api.put<{ customValues: Values }>(`/tasks/${taskId}/custom-fields/${field.id}`, { value });
      onChanged(res.customValues);
      return true;
    } catch (error: unknown) {
      toast(error instanceof ApiError ? error.message : `Could not save ${field.name}.`, { tone: "danger" });
      return false;
    }
  };

  if (fields.length === 0) return null;
  return (
    <div className="task-detail__section">
      <h3 className="task-detail__section-title">Custom fields</h3>
      <div className="task-detail__field-row" style={{ flexWrap: "wrap" }}>
        {fields.map((field) => (
          <FieldInput key={field.id} field={field} value={values[field.id]} canEdit={canEdit} people={people} save={save} />
        ))}
      </div>
    </div>
  );
}

function FieldInput({
  field,
  value,
  canEdit,
  people,
  save,
}: {
  field: CustomField;
  value: string | number | undefined;
  canEdit: boolean;
  people: Person[];
  save: (field: CustomField, value: string | number | null) => Promise<boolean>;
}) {
  const shown = value === undefined ? "" : String(value);
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const known = useMemo(() => people.some((p) => p.id === shown), [people, shown]);

  if (field.type === "dropdown") {
    return (
      <Select label={field.name} value={shown} disabled={!canEdit} onChange={(e) => void save(field, e.target.value || null)}>
        <option value="">Not set</option>
        {field.options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </Select>
    );
  }
  if (field.type === "person") {
    return (
      <Select label={field.name} value={shown} disabled={!canEdit} onChange={(e) => void save(field, e.target.value || null)}>
        <option value="">Not set</option>
        {shown && !known && <option value={shown}>Unknown person</option>}
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.displayName}
          </option>
        ))}
      </Select>
    );
  }
  const commit = async () => {
    if (draft === shown) return;
    if (field.type === "number") {
      const n = parseNumberInput(draft);
      if (n === undefined) return setDraft(shown);
      if (!(await save(field, n))) setDraft(shown);
      return;
    }
    if (field.type === "date") {
      if (!(await save(field, draft || null))) setDraft(shown);
      return;
    }
    if (!(await save(field, draft.trim() === "" ? null : draft))) setDraft(shown);
  };
  return (
    <Input
      label={field.name}
      type={field.type === "date" ? "date" : "text"}
      inputMode={field.type === "number" ? "decimal" : undefined}
      value={draft}
      disabled={!canEdit}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
    />
  );
}
